import { Component, ElementRef, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ClassInput } from '@feedonomics/frontend-components';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';

const DEFAULT_COLLAPSED_ROWS: number = 3;

@Component({
    selector: 'fdx-collapsible-code',
    templateUrl: './collapsible-code.component.html',
    styleUrl: './collapsible-code.component.scss',
    standalone: false
})
export class CollapsibleCodeComponent implements OnChanges {

    @Input() content: string = '';
    @Input() readonly: boolean = false;
    @Input() overflowScroll: boolean = false;
    /**
     * Adjusts the UI to have extra padding when this input is used in a card design
     * rather than a condensed input design
     */
    @Input() extraPadding: boolean = false;
    /**
     * Number of content rows to show when collapsed. Also raises the newline
     * threshold at which the expand/contract toggle is displayed.
     */
    @Input() collapsedRows: number = DEFAULT_COLLAPSED_ROWS;

    isCollapsed: boolean = true;

    readonly downIcon: IconDefinition = faChevronDown;
    readonly upIcon: IconDefinition = faChevronUp;

    constructor(
        public readonly element: ElementRef<HTMLElement>
    ) { }

    ngOnChanges(changes: SimpleChanges): void {
        if ('content' in changes) {
            this.bindContent();
        }
    }

    toggle(): void {
        const codeMirrorScroll = this.element.nativeElement.querySelector('div.CodeMirror-scroll');

        if (codeMirrorScroll) {
            codeMirrorScroll.scrollTop = 0;
            codeMirrorScroll.scrollLeft = 0;
        }

        this.isCollapsed = !this.isCollapsed;
    }

    get showToggle(): boolean {
        return this.content && (this.content.match(/\n/g) || []).length >= this.collapsedRows;
    }

    get toggleText(): string {
        return this.isCollapsed ? 'Expand' : 'Contract';
    }

    get classes(): ClassInput {
        return {
            'code-collapsed': this.isCollapsed,
            'readonly': this.readonly,
            'codemirror-overflow-v-scroll': this.overflowScroll,
            'extra-padding': this.extraPadding
        };
    }

    get icon(): IconDefinition {
        return this.isCollapsed ? this.downIcon : this.upIcon;
    }

    private bindContent(): void {
        if (this.isCollapsed && !this.showToggle) {
            this.isCollapsed = false;
        }
    }
}
